import { useState } from 'react'
import { CheckCircle2, XCircle, Clock, Package, ArrowUpCircle, ArrowDownCircle, AlertCircle, RefreshCw } from 'lucide-react'
import { useSubscription } from '@/hooks/use-subscription'
import { useActiveUpgradeRequest, useAvailablePlans } from '@/hooks/use-subscription-upgrade'
import type { AvailablePlan } from '@/hooks/use-subscription-upgrade'
import UpgradeModal from '@/components/subscription/upgrade-modal'

// ── Feature groups ─────────────────────────────────────────────────────────────

const FEATURE_GROUPS = [
  {
    group: 'Konten',
    keys: [
      { key: 'members', label: 'Manajemen Member' },
      { key: 'events',  label: 'Manajemen Event'  },
      { key: 'music',   label: 'Musik'            },
      { key: 'shop',    label: 'Shop'             },
      { key: 'media',   label: 'Media Library'    },
    ],
  },
  {
    group: 'Cheki',
    keys: [
      { key: 'cheki_form',      label: 'Cheki Form'    },
      { key: 'cheki_analytics', label: 'Cheki Analitik' },
    ],
  },
  {
    group: 'Halaman Publik',
    keys: [
      { key: 'theme_preset',  label: 'Tema Preset'     },
      { key: 'custom_theme',  label: 'Tema Visual'     },
      { key: 'gallery',       label: 'Galeri Komponen' },
      { key: 'custom_pages',  label: 'Halaman Kustom'  },
    ],
  },
]

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function formatPrice(price: number | null) {
  if (!price) return 'Gratis'
  return (
    new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      maximumFractionDigits: 0,
    }).format(price) + ' / bulan'
  )
}

function getDaysRemaining(iso: string): number {
  const diff = new Date(iso).getTime() - Date.now()
  return Math.ceil(diff / (1000 * 60 * 60 * 24))
}

// ── Status configs ─────────────────────────────────────────────────────────────

const SUBSCRIPTION_STATUS_CONFIG = {
  active: {
    label: 'Aktif',
    className: 'bg-green-500/10 text-green-600',
    icon: CheckCircle2,
  },
  trial: {
    label: 'Trial',
    className: 'bg-blue-500/10 text-blue-600',
    icon: Clock,
  },
  suspended: {
    label: 'Ditangguhkan',
    className: 'bg-yellow-500/10 text-yellow-600',
    icon: Clock,
  },
  cancelled: {
    label: 'Dibatalkan',
    className: 'bg-destructive/10 text-destructive',
    icon: XCircle,
  },
}

const UPGRADE_STATUS_CONFIG = {
  pending: {
    label: 'Menunggu review admin',
    className: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
    icon: Clock,
  },
  proof_uploaded: {
    label: 'Bukti terkirim — menunggu verifikasi',
    className: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
    icon: Clock,
  },
  approved: {
    label: 'Permintaan disetujui — akun sedang diupgrade',
    className: 'bg-green-500/10 text-green-600 border-green-500/20',
    icon: CheckCircle2,
  },
  rejected: {
    label: 'Permintaan ditolak',
    className: 'bg-destructive/10 text-destructive border-destructive/20',
    icon: XCircle,
  },
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function SubscriptionPage() {
  const { data: subscription, isLoading, error } = useSubscription()
  const { data: activeRequest } = useActiveUpgradeRequest()
  const { data: availablePlans = [] } = useAvailablePlans()

  const [activeTab, setActiveTab] = useState<'current' | 'plans'>('current')
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<AvailablePlan | null>(null)
  const [isDowngrade, setIsDowngrade] = useState(false)
  const [reuploadForRequest, setReuploadForRequest] = useState<string | undefined>(undefined)

  if (isLoading) return <p className="text-muted-foreground">Memuat informasi langganan...</p>
  if (error) return <p className="text-sm text-destructive">Gagal memuat informasi langganan.</p>

  if (!subscription) {
    return (
      <div className="max-w-2xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Paket Langganan</h1>
          <p className="text-muted-foreground">Informasi paket aktif akun Anda.</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-6 text-center space-y-2">
          <Package className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Belum ada paket yang ditetapkan. Hubungi administrator platform.
          </p>
        </div>
      </div>
    )
  }

  const isTrial = subscription.status === 'trial'
  const isExpired = !!subscription.expires_at && new Date(subscription.expires_at) < new Date()
  const daysRemaining = subscription.expires_at ? getDaysRemaining(subscription.expires_at) : null

  const subStatus = SUBSCRIPTION_STATUS_CONFIG[subscription.status] ?? SUBSCRIPTION_STATUS_CONFIG.active
  const SubStatusIcon = subStatus.icon
  const features = subscription.plans?.plan_features ?? []

  const currentAvailablePlan = availablePlans.find((p) => p.id === subscription.plans?.id)
  const currentSortOrder = currentAvailablePlan?.sort_order ?? 0
  const planLimits = subscription.plans?.plan_limits ?? []
  const hasLimits = planLimits.length > 0

  // Buttons locked when there's an active request (except for expired — allow re-purchase)
  const actionsLocked = !!activeRequest && !isExpired

  function openUpgradeModal(plan: AvailablePlan, downgrade = false) {
    setSelectedPlan(plan)
    setIsDowngrade(downgrade)
    setReuploadForRequest(undefined)
    setModalOpen(true)
  }

  function openReuploadModal() {
    if (!activeRequest) return
    setSelectedPlan(activeRequest.plans as AvailablePlan | null ?? null)
    setIsDowngrade(false)
    setReuploadForRequest(activeRequest.id)
    setModalOpen(true)
  }

  function handleModalClose() {
    setModalOpen(false)
    setSelectedPlan(null)
    setIsDowngrade(false)
    setReuploadForRequest(undefined)
  }

  return (
    <div className="max-w-2xl space-y-6">
      {/* ── Page header ─────────────────────────────────────────────────────── */}
      <div>
        <h1 className="text-2xl font-bold">Paket Langganan</h1>
        <p className="text-muted-foreground">Informasi paket aktif akun Anda.</p>
      </div>

      {/* ── Tabs ────────────────────────────────────────────────────────────── */}
      <div className="border-b border-border">
        <div className="flex gap-0">
          {(
            [
              { id: 'current', label: 'Paket Aktif' },
              { id: 'plans',   label: 'Semua Paket'  },
            ] as const
          ).map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`relative px-4 py-2.5 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
              {activeTab === tab.id && (
                <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-primary" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          TAB 1 — Paket Aktif
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'current' && (
        <div className="space-y-5">
          {/* Expired banner */}
          {isExpired && (
            <div className="rounded-xl border border-destructive/20 bg-destructive/5 px-4 py-4 flex items-start gap-3">
              <XCircle className="h-4 w-4 shrink-0 mt-0.5 text-destructive" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-destructive">Paket kamu sudah berakhir</p>
                <p className="text-xs mt-0.5 text-destructive/80">
                  Semua fitur dinonaktifkan. Perpanjang atau upgrade paket untuk kembali menggunakannya.
                </p>
              </div>
            </div>
          )}

          {/* Active request banner */}
          {activeRequest && !isExpired && (
            <UpgradeRequestBanner request={activeRequest} onReupload={openReuploadModal} />
          )}

          {/* Plan card */}
          <div className="rounded-xl border border-border bg-card p-6 space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xl font-semibold">
                  {subscription.plans?.name ?? 'Paket tidak diketahui'}
                </p>
                {subscription.plans?.description && (
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {subscription.plans.description}
                  </p>
                )}
              </div>
              {isExpired ? (
                <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium shrink-0 bg-destructive/10 text-destructive">
                  <XCircle className="h-3.5 w-3.5" />
                  Berakhir
                </span>
              ) : (
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium shrink-0 ${subStatus.className}`}
                >
                  <SubStatusIcon className="h-3.5 w-3.5" />
                  {subStatus.label}
                </span>
              )}
            </div>

            <div className="border-t border-border pt-4 grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground text-xs uppercase tracking-wide font-medium mb-0.5">
                  Harga
                </p>
                <p className="font-medium">{formatPrice(subscription.plans?.price_idr ?? null)}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs uppercase tracking-wide font-medium mb-0.5">
                  Mulai berlangganan
                </p>
                <p className="font-medium">{formatDate(subscription.started_at)}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs uppercase tracking-wide font-medium mb-0.5">
                  {isTrial ? 'Trial berakhir' : 'Berakhir'}
                </p>
                <p className={`font-medium ${isExpired ? 'text-destructive' : ''}`}>
                  {subscription.expires_at ? formatDate(subscription.expires_at) : 'Tidak terbatas'}
                </p>
              </div>
            </div>

            {/* Trial countdown */}
            {isTrial && subscription.expires_at && (
              <div
                className={`rounded-lg px-3 py-2.5 text-xs flex items-center gap-2 ${
                  isExpired
                    ? 'bg-destructive/5 border border-destructive/20 text-destructive'
                    : daysRemaining !== null && daysRemaining <= 3
                    ? 'bg-yellow-500/10 border border-yellow-500/20 text-yellow-600'
                    : 'bg-blue-500/5 border border-blue-500/20 text-blue-600'
                }`}
              >
                <Clock className="h-3.5 w-3.5 shrink-0" />
                {isExpired
                  ? 'Masa trial sudah berakhir. Beli paket untuk melanjutkan.'
                  : daysRemaining === 0
                  ? 'Trial berakhir hari ini.'
                  : daysRemaining === 1
                  ? 'Trial berakhir besok.'
                  : `Trial berakhir dalam ${daysRemaining} hari.`}
              </div>
            )}
          </div>

          {/* Feature list */}
          <div className="space-y-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Fitur Paket
            </h2>
            {isExpired && (
              <p className="text-xs text-destructive">
                Semua fitur dinonaktifkan karena paket sudah berakhir.
              </p>
            )}
            {FEATURE_GROUPS.map(({ group, keys }) => (
              <div key={group} className="space-y-1.5">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {group}
                </p>
                <div className="rounded-xl border border-border bg-card divide-y divide-border">
                  {keys.map(({ key, label }) => {
                    const active = !isExpired && features.some((f) => f.feature_key === key)
                    return (
                      <div key={key} className="flex items-center gap-3 px-4 py-3">
                        {active ? (
                          <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                        ) : (
                          <XCircle className="h-4 w-4 text-muted-foreground/40 shrink-0" />
                        )}
                        <span className={`text-sm ${active ? '' : 'text-muted-foreground'}`}>
                          {label}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Plan limits (shown for plans with hard limits, e.g. Free) */}
          {hasLimits && (
            <div className="space-y-2">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Batas Penggunaan
              </h2>
              <div className="rounded-xl border border-border bg-card divide-y divide-border">
                {planLimits.map(({ limit_key, limit_value }) => {
                  const LIMIT_LABELS: Record<string, string> = {
                    max_members: 'Idol Member',
                    max_events: 'Event',
                    storage_mb: 'Storage',
                  }
                  const label = LIMIT_LABELS[limit_key] ?? limit_key
                  const display = limit_value === -1
                    ? 'Tidak terbatas'
                    : limit_key === 'storage_mb'
                    ? `${limit_value} MB`
                    : `Maks. ${limit_value}`
                  return (
                    <div key={limit_key} className="flex items-center justify-between px-4 py-3">
                      <span className="text-sm">{label}</span>
                      <span className="text-sm font-medium text-muted-foreground">{display}</span>
                    </div>
                  )
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                Upgrade ke Basic atau Pro untuk batas yang lebih tinggi.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          TAB 2 — Semua Paket
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'plans' && (
        <div className="space-y-4">
          {/* Locked notice when there's an active request */}
          {actionsLocked && (
            <div className="flex items-start gap-2 rounded-lg border border-yellow-500/20 bg-yellow-500/5 px-4 py-3 text-sm text-yellow-700">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                Ada permintaan perubahan paket yang sedang diproses. Kamu tidak bisa mengajukan permintaan baru sampai permintaan sebelumnya selesai.
              </span>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {availablePlans.map((plan) => {
              const isCurrent = plan.id === subscription.plans?.id
              const isHigher = plan.sort_order > currentSortOrder
              const isLower = plan.sort_order < currentSortOrder
              const isTrialSamePlan = isTrial && isCurrent
              const planFeatureKeys = plan.plan_features.map((f) => f.feature_key)

              return (
                <div
                  key={plan.id}
                  className={`rounded-xl border bg-card flex flex-col ${
                    isCurrent ? 'border-primary/50 ring-1 ring-primary/20' : 'border-border'
                  }`}
                >
                  {/* Card header */}
                  <div className="px-5 pt-5 pb-4 border-b border-border space-y-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-semibold text-base leading-tight">{plan.name}</p>
                      {isCurrent && (
                        <span className="inline-flex shrink-0 items-center rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                          Paket kamu
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-medium text-muted-foreground">
                      {formatPrice(plan.price_idr)}
                    </p>
                    {plan.description && (
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {plan.description}
                      </p>
                    )}
                  </div>

                  {/* Feature list */}
                  <div className="px-5 py-4 flex-1 space-y-3">
                    {FEATURE_GROUPS.map(({ group, keys }) => (
                      <div key={group}>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                          {group}
                        </p>
                        <div className="space-y-1">
                          {keys.map(({ key, label }) => {
                            const active = planFeatureKeys.includes(key)
                            return (
                              <div key={key} className="flex items-center gap-2">
                                {active ? (
                                  <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />
                                ) : (
                                  <XCircle className="h-3.5 w-3.5 text-muted-foreground/30 shrink-0" />
                                )}
                                <span
                                  className={`text-xs ${
                                    active ? 'text-foreground' : 'text-muted-foreground/60'
                                  }`}
                                >
                                  {label}
                                </span>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <div className="px-5 pb-5">
                    {isCurrent && !isTrial ? (
                      // Active non-trial on current plan — can renew early to extend expires_at
                      <button
                        type="button"
                        disabled={actionsLocked}
                        onClick={() => openUpgradeModal(plan, false)}
                        className="flex w-full items-center justify-center gap-1.5 rounded-md border border-primary/40 bg-primary/5 px-4 py-2 text-xs font-medium text-primary hover:bg-primary/10 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <RefreshCw className="h-3.5 w-3.5" />
                        Perpanjang
                      </button>
                    ) : isTrialSamePlan ? (
                      // Trial converting to same tier
                      <button
                        type="button"
                        disabled={actionsLocked}
                        onClick={() => openUpgradeModal(plan, false)}
                        className="flex w-full items-center justify-center gap-1.5 rounded-md bg-primary px-4 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <ArrowUpCircle className="h-3.5 w-3.5" />
                        Beli
                      </button>
                    ) : isHigher ? (
                      <button
                        type="button"
                        disabled={actionsLocked}
                        onClick={() => openUpgradeModal(plan, false)}
                        className="flex w-full items-center justify-center gap-1.5 rounded-md bg-primary px-4 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <ArrowUpCircle className="h-3.5 w-3.5" />
                        Upgrade
                      </button>
                    ) : isLower ? (
                      <button
                        type="button"
                        disabled={actionsLocked}
                        onClick={() => openUpgradeModal(plan, true)}
                        className="flex w-full items-center justify-center gap-1.5 rounded-md border border-border px-4 py-2 text-xs font-medium hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <ArrowDownCircle className="h-3.5 w-3.5" />
                        Downgrade
                      </button>
                    ) : null}
                  </div>
                </div>
              )
            })}
          </div>

          <p className="text-xs text-muted-foreground">
            Paket aktif selama 30 hari sejak pembayaran diverifikasi admin. Downgrade tidak memerlukan bukti pembayaran.
          </p>
        </div>
      )}

      {/* ── Upgrade modal ───────────────────────────────────────────────────── */}
      {selectedPlan && (
        <UpgradeModal
          open={modalOpen}
          onClose={handleModalClose}
          selectedPlan={selectedPlan}
          currentPlanId={subscription.plans?.id ?? null}
          requestId={reuploadForRequest}
          isDowngrade={isDowngrade}
        />
      )}
    </div>
  )
}

// ── Upgrade request status banner ──────────────────────────────────────────────

interface UpgradeRequestBannerProps {
  request: {
    id: string
    status: 'pending' | 'proof_uploaded' | 'approved' | 'rejected'
    admin_note: string | null
    plans: { id: string; name: string; slug: string; price_idr: number | null } | null
  }
  onReupload: () => void
}

function UpgradeRequestBanner({ request, onReupload }: UpgradeRequestBannerProps) {
  const config = UPGRADE_STATUS_CONFIG[request.status]
  if (!config) return null

  const StatusIcon = config.icon
  const planName = request.plans?.name ?? 'paket baru'

  return (
    <div className={`rounded-xl border px-4 py-4 space-y-3 ${config.className}`}>
      <div className="flex items-start gap-3">
        <StatusIcon className="h-4 w-4 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">
            Permintaan perubahan paket ke {planName}
          </p>
          <p className="text-xs mt-0.5 opacity-80">
            {config.label}
          </p>
        </div>
      </div>

      {request.status === 'rejected' && (
        <div className="space-y-2">
          {request.admin_note && (
            <div className="rounded-md bg-destructive/5 border border-destructive/10 px-3 py-2">
              <p className="text-xs font-medium mb-0.5">Catatan admin:</p>
              <p className="text-xs opacity-90">{request.admin_note}</p>
            </div>
          )}
          <button
            type="button"
            onClick={onReupload}
            className="inline-flex items-center gap-1.5 rounded-md bg-destructive/10 border border-destructive/20 px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/20 transition-colors"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Kirim Ulang Bukti Pembayaran
          </button>
        </div>
      )}

      {request.status !== 'rejected' && (
        <div className="flex items-center gap-1.5 text-xs opacity-70">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          Paket akan diperbarui setelah admin memverifikasi permintaan.
        </div>
      )}
    </div>
  )
}
